import { Sparkles } from "lucide-react";
import type { Locale, TranslationKey } from "../i18n";
import {
  localizeDirection,
  localizePurposeForLocale,
  localizeRationale,
  t,
} from "../i18n";
import type { ImagePlanSlot } from "../types/workspace";

type SlotEditorProps = {
  slot: ImagePlanSlot;
  locale: Locale;
  onUpdate: (patch: Partial<ImagePlanSlot>) => void;
};

const directions = [
  "Clean Studio",
  "Premium",
  "Lifestyle",
  "Editorial",
  "Warm Minimal",
];
const protections: Array<[keyof ImagePlanSlot["preserve"], TranslationKey]> = [
  ["shape", "productShape"],
  ["logo", "brandMark"],
  ["colours", "productColour"],
  ["packaging", "packagingText"],
];

export function SlotEditor({ slot, locale, onUpdate }: SlotEditorProps) {
  return (
    <div className="slot-editor">
      <div className="editor-title">
        <span className="editor-spark">
          <Sparkles size={15} aria-hidden="true" />
        </span>
        <div>
          <small>{t(locale, "adjusting")}</small>
          <h2>{localizePurposeForLocale(locale, slot.purpose)}</h2>
        </div>
      </div>
      <p className="editor-copy">
        {t(locale, "whyThisImage")}: {localizeRationale(locale, slot.rationale)}
      </p>
      <label className="note-field">
        {t(locale, "imagePurpose")}
        <input
          value={slot.purpose}
          onChange={(event) => onUpdate({ purpose: event.target.value })}
        />
      </label>
      <fieldset>
        <legend>{t(locale, "visualDirection")}</legend>
        <div className="direction-chips">
          {directions.map((direction) => (
            <button
              type="button"
              key={direction}
              className={slot.direction === direction ? "selected" : ""}
              aria-pressed={slot.direction === direction}
              onClick={() => onUpdate({ direction })}
            >
              {localizeDirection(locale, direction)}
            </button>
          ))}
        </div>
      </fieldset>
      <fieldset>
        <legend>{t(locale, "protectInstructions")}</legend>
        {protections.map(([fact, label]) => (
          <label className="preserve-toggle" key={fact}>
            <input
              type="checkbox"
              checked={slot.preserve[fact]}
              onChange={(event) =>
                onUpdate({
                  preserve: { ...slot.preserve, [fact]: event.target.checked },
                })
              }
            />
            {t(locale, label)}
          </label>
        ))}
      </fieldset>
      <label className="note-field">
        {t(locale, "imageNote")}
        <textarea
          className="direction-note"
          value={slot.note}
          onChange={(event) => onUpdate({ note: event.target.value })}
          placeholder={t(locale, "imageNotePlaceholder")}
        />
      </label>
    </div>
  );
}
